import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { 
  Calendar, 
  Clock, 
  ArrowLeft, 
  AlertCircle, 
  CheckCircle2, 
  Timer, 
  Sparkles,
  HelpCircle,
  Trash2,
  X,
  MessageCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { TransactionalChat } from '@/modules/professional/components/chat/TransactionalChat';
import { PushService } from '@/core/infrastructure/push/PushService';
import { ReviewReminderBanner } from '@/modules/consumer/components/ReviewReminderBanner';

// ─── Tipos ───────────────────────────────────────────────────────────────────

interface Cita {
  cita_id: string;
  fecha_hora_inicio: string;
  fecha_hora_fin: string;
  estado: string;
  expires_at: string | null;
  profesional_uuid: string;
  paciente_uuid: string;
  servicio_id: number;
  servicio_titulo: string;
  servicio_precio: number;
  servicio_moneda: string;
  profesional_nombre: string;
  profesional_titulo: string;
  profesional_avatar: string | null;
}

type TabType = 'por_pagar' | 'pendientes' | 'proximas' | 'historial';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const DIAS_ES = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
const MESES_ES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function formatFechaCompleta(iso: string): string {
  const d = new Date(iso);
  const diaSemana = DIAS_ES[d.getDay()];
  const diaNum = d.getDate();
  const mes = MESES_ES[d.getMonth()];
  const año = d.getFullYear();
  
  const h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  const minStr = String(d.getMinutes()).padStart(2, '0');

  return `${diaSemana}, ${diaNum} de ${mes} ${año} · ${h12}:${minStr} ${ampm}`;
}

const ESTADOS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: any }> = {
  reservada_ttl:               { label: 'Por Pagar', color: '#D97706', bg: 'rgba(217, 119, 6, 0.15)', icon: Timer },
  pendiente_verificacion_pago: { label: 'En Verificación', color: '#2563EB', bg: 'rgba(37, 99, 235, 0.15)', icon: Clock },
  en_revision:                 { label: 'En Revisión', color: '#2563EB', bg: 'rgba(37, 99, 235, 0.15)', icon: Clock },
  confirmada:                  { label: 'Confirmada', color: '#059669', bg: 'rgba(5, 150, 105, 0.15)', icon: CheckCircle2 },
  completada:                  { label: 'Realizada', color: '#64748B', bg: 'rgba(100, 116, 139, 0.15)', icon: CheckCircle2 },
  expirada:                    { label: 'Expirada', color: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)', icon: AlertCircle },
  rechazada:                   { label: 'Rechazada', color: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)', icon: AlertCircle },
  cancelada_paciente:          { label: 'Cancelada', color: '#64748B', bg: 'rgba(100, 116, 139, 0.15)', icon: HelpCircle },
};

// ─── Temporizador en Vivo para Citas Reservadas (TTL) ───────────────────────────

function LiveCountdown({ expiresAt, onExpire }: Readonly<{ expiresAt: string; onExpire: () => void }>) {
  const [segundos, setSegundos] = useState(0);

  useEffect(() => {
    const calcularRestante = () => {
      const diff = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setSegundos(diff);
      if (diff <= 0) {
        onExpire();
      }
    };

    calcularRestante();
    const interval = setInterval(calcularRestante, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, onExpire]);

  if (segundos <= 0) {
    return <span style={{ color: '#EF4444', fontWeight: 800 }}>Expirando...</span>;
  }

  const mins = Math.floor(segundos / 60);
  const secs = segundos % 60;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#F59E0B', backgroundColor: 'rgba(245, 158, 11, 0.1)', padding: '6px 12px', borderRadius: '100px', fontSize: '13px', fontWeight: 700 }}>
      <Timer size={14} className="pulse-timer" />
      <span>Paga en {mins}:{String(secs).padStart(2, '0')}</span>
    </div>
  );
}

// ─── Tarjeta de Cita ──────────────────────────────────────────────────────────

function CitaCard({ cita, onExpire, onPagar, currentUserId, onShowToast }: Readonly<{ cita: Cita; onExpire: () => void; onPagar: (c: Cita) => void; currentUserId: string | null; onShowToast: (msg: string) => void; }>) {
  const cfg = ESTADOS_CONFIG[cita.estado] ?? { label: cita.estado, color: '#64748B', bg: 'rgba(100, 116, 139, 0.15)', icon: HelpCircle };
  const StatusIcon = cfg.icon;

  const isPorPagar = cita.estado === 'reservada_ttl';
  const showChat = ['pendiente_verificacion_pago', 'en_revision', 'confirmada', 'pago_parcial', 'en_disputa'].includes(cita.estado);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);

  // Escuchar mensajes en tiempo real cuando el chat está colapsado (Notificación In-App)
  useEffect(() => {
    if (isChatOpen || !showChat || !currentUserId) {
      if (isChatOpen) setHasUnread(false);
      return;
    }

    const channelName = `chat_transaccional:${cita.cita_id}`;
    const channel = supabase.channel(channelName)
      .on('broadcast', { event: 'new_message' }, (payload) => {
        const msg = payload.payload;
        if (msg.emisor_id !== currentUserId) {
          setHasUnread(true);
          onShowToast(`Nuevo mensaje en la cita con ${cita.profesional_nombre}`);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isChatOpen, showChat, cita.cita_id, currentUserId, cita.profesional_nombre, onShowToast]);

  return (
    <div style={{
      backgroundColor: '#1E293B',
      borderRadius: '20px',
      border: isPorPagar ? '1.5px dashed #F59E0B' : '1px solid #334155',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
      transition: 'transform 0.2s ease, border-color 0.2s',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Cabecera Tarjeta */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {cita.profesional_avatar ? (
            <img src={cita.profesional_avatar} alt={cita.profesional_nombre} style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #3B82F6' }} />
          ) : (
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', backgroundColor: '#3B82F6', display: 'flex', justifyContent: 'center', alignItems: 'center', color: '#FFFFFF', fontSize: '18px', fontWeight: 800 }}>
              {cita.profesional_nombre.charAt(0)}
            </div>
          )}
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 800, color: '#FFFFFF', margin: 0 }}>{cita.profesional_nombre}</h3>
            <p style={{ fontSize: '13px', color: '#94A3B8', margin: '2px 0 0 0' }}>{cita.profesional_titulo}</p>
          </div>
        </div>

        {isPorPagar && cita.expires_at ? (
          <LiveCountdown expiresAt={cita.expires_at} onExpire={onExpire} />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: cfg.color, backgroundColor: cfg.bg, padding: '4px 12px', borderRadius: '100px', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
            <StatusIcon size={12} />
            <span>{cfg.label}</span>
          </div>
        )}
      </div>

      {/* Detalle Servicio / Fecha */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid #334155', paddingTop: '14px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: '15px', color: '#E2E8F0', fontWeight: 700 }}>{cita.servicio_titulo}</span>
          <span style={{ fontSize: '15px', color: '#3B82F6', fontWeight: 800 }}>
            {cita.servicio_moneda === 'EUR' ? '€' : '$'}{Number(cita.servicio_precio).toFixed(2)}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#94A3B8', fontSize: '13px', marginTop: '4px' }}>
          <Calendar size={14} color="#3B82F6" />
          <span>{formatFechaCompleta(cita.fecha_hora_inicio)}</span>
        </div>
      </div>

      {cita.estado === 'completada' && currentUserId && (
        <ReviewReminderBanner
          citaId={cita.cita_id}
          profesionalId={cita.profesional_uuid}
          pacienteId={cita.paciente_uuid}
          fechaHoraInicio={cita.fecha_hora_inicio}
          onSuccess={() => onShowToast('¡Gracias por tu calificación!')}
          onDismiss={() => {}}
        />
      )}

      {/* Botones de acción directa (0 clicks adicionales) */}
      {isPorPagar && (
        <button 
          onClick={() => onPagar(cita)}
          style={{
            backgroundColor: '#3B82F6',
            color: '#FFFFFF',
            fontWeight: 800,
            fontSize: '15px',
            padding: '14px',
            borderRadius: '14px',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            transition: 'background-color 0.2s',
            width: '100%'
          }}
        >
          <Sparkles size={16} />
          Completar Pago Ahora
        </button>
      )}

      {/* Chat Transaccional Desplegable */}
      {showChat && (
        <div style={{ borderTop: '1px solid #334155', paddingTop: '16px', marginTop: '8px' }}>
          <button
            onClick={() => setIsChatOpen(!isChatOpen)}
            style={{
              position: 'relative',
              backgroundColor: isChatOpen ? 'rgba(59, 130, 246, 0.15)' : '#334155',
              color: isChatOpen ? '#3B82F6' : '#E2E8F0',
              fontWeight: 700,
              fontSize: '14px',
              padding: '12px 16px',
              borderRadius: '12px',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              width: '100%',
              transition: 'all 0.2s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ position: 'relative' }}>
                <MessageCircle size={18} />
                {hasUnread && (
                  <div style={{ 
                    position: 'absolute', top: '-2px', right: '-2px', width: '8px', height: '8px', 
                    borderRadius: '50%', backgroundColor: '#EF4444', border: '1px solid #1E293B',
                    animation: 'pulse 2s infinite' 
                  }} />
                )}
              </div>
              <span>Soporte con Profesional</span>
            </div>
            {isChatOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
          
          {isChatOpen && (
            <div style={{ marginTop: '16px', animation: 'fadeIn 0.2s ease-out' }}>
              <TransactionalChat 
                citaId={cita.cita_id} 
                profesionalUuid={cita.profesional_uuid} 
                pacienteUuid={cita.paciente_uuid} 
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Componente Principal ─────────────────────────────────────────────────────

export function MisCitasScreen() {
  const navigate = useNavigate();
  const [citas, setCitas] = useState<Cita[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('por_pagar');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [globalToast, setGlobalToast] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);

  const showToast = useCallback((msg: string) => {
    setGlobalToast(msg);
    setTimeout(() => setGlobalToast(null), 4000);
  }, []);

  const cargarCitas = useCallback(async (silencioso = false, cargarMas = false) => {
    if (silencioso) {
      // No emitimos loading si es silencioso
    } else {
      setLoading(true);
    }

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const user = sessionData?.session?.user;
      if (!user) {
        setLoading(false);
        return;
      }
      setCurrentUserId(user.id);

      const currentPage = cargarMas ? page + 1 : 0;
      const limit = 15;
      const from = currentPage * limit;
      const to = from + limit - 1;

      const { data, error, count } = await supabase
        .from('agendamiento_citas')
        .select(`
          cita_id,
          fecha_hora_inicio,
          fecha_hora_fin,
          estado,
          expires_at,
          profesional_uuid,
          paciente_uuid,
          servicio_id,
          profesional_servicios_faq (
            titulo_opcion,
            precio_sugerido,
            moneda
          ),
          perfiles_profesionales!profesional_uuid (
            titulo_principal,
            perfiles_usuario (
              nombres_completos,
              apellidos_completos,
              avatar_url
            )
          )
        `, { count: 'exact' })
        .eq('paciente_uuid', user.id)
        .eq('oculto_paciente', false)
        .order('fecha_hora_inicio', { ascending: false })
        .range(from, to);

      if (error) throw error;

      if (data) {
        const mapeadas: Cita[] = data.map((c: any) => {
          const profUsr = c.perfiles_profesionales?.perfiles_usuario;
          const profNombre = profUsr 
            ? `${profUsr.nombres_completos} ${profUsr.apellidos_completos || ''}`.trim()
            : 'Profesional';
          
          return {
            cita_id: c.cita_id,
            fecha_hora_inicio: c.fecha_hora_inicio,
            fecha_hora_fin: c.fecha_hora_fin,
            estado: c.estado,
            expires_at: c.expires_at,
            profesional_uuid: c.profesional_uuid,
            paciente_uuid: c.paciente_uuid,
            servicio_id: c.servicio_id,
            servicio_titulo: c.profesional_servicios_faq?.titulo_opcion || 'Consulta General',
            servicio_precio: c.profesional_servicios_faq?.precio_sugerido || 0,
            servicio_moneda: c.profesional_servicios_faq?.moneda || 'USD',
            profesional_nombre: profNombre,
            profesional_titulo: c.perfiles_profesionales?.titulo_principal || 'Especialista',
            profesional_avatar: profUsr?.avatar_url || null
          };
        });
        
        if (cargarMas) {
          setCitas(prev => [...prev, ...mapeadas]);
        } else {
          setCitas(mapeadas);
        }
        setPage(currentPage);
        setHasMore(count ? from + data.length < count : false);
      }
    } catch (e) {
      console.error('[MisCitasScreen] Error cargando citas:', e);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    cargarCitas();
    PushService.registerAndSubscribe();
  }, [cargarCitas]);

  const handlePagar = (cita: Cita) => {
    navigate('/app/Booking', {
      state: {
        profesionalUuid: cita.profesional_uuid,
        profesionalNombre: cita.profesional_nombre,
        profesionalTitulo: cita.profesional_titulo
      }
    });
  };

  // Filtrado de citas según la pestaña activa
  const filtradas = citas.filter(c => {
    if (activeTab === 'por_pagar') {
      return c.estado === 'reservada_ttl';
    }
    if (activeTab === 'pendientes') {
      return ['pendiente_verificacion_pago', 'en_revision'].includes(c.estado);
    }
    if (activeTab === 'proximas') {
      return c.estado === 'confirmada';
    }
    return ['completada', 'expirada', 'rechazada', 'cancelada_paciente'].includes(c.estado);
  });

  const contarPorPagar = citas.filter(c => c.estado === 'reservada_ttl').length;

  const handleLimpiarHistorial = () => {
    setShowConfirmModal(true);
  };

  const confirmarLimpiarHistorial = async () => {
    if (!currentUserId) return;
    
    const idsToHide = filtradas.map(c => c.cita_id);
    if (idsToHide.length === 0) {
      setShowConfirmModal(false);
      return;
    }

    const { error } = await supabase
      .from('agendamiento_citas')
      .update({ oculto_paciente: true })
      .in('cita_id', idsToHide)
      .eq('paciente_uuid', currentUserId);

    if (error) {
      console.error('[MisCitasScreen] Error limpiando historial:', error);
      showToast('Error al limpiar el historial');
    } else {
      const idsSet = new Set(idsToHide);
      setCitas(prev => prev.filter(c => !idsSet.has(c.cita_id)));
      showToast('Historial limpiado');
    }
    
    setShowConfirmModal(false);
  };

  const getEmptyStateTitle = () => {
    if (activeTab === 'por_pagar') return '¡Todo pagado!';
    if (activeTab === 'pendientes') return 'Sin revisiones';
    if (activeTab === 'proximas') return 'Sin citas programadas';
    return 'Historial vacío';
  };

  const getEmptyStateDescription = () => {
    if (activeTab === 'por_pagar') {
      return 'No tienes citas con pagos pendientes de confirmación.';
    }
    if (activeTab === 'pendientes') {
      return 'Tus pagos subidos a la espera de verificación del doctor aparecerán aquí.';
    }
    if (activeTab === 'proximas') {
      return 'Tus citas verificadas y confirmadas aparecerán aquí.';
    }
    return 'Aquí se guardará el registro de tus consultas finalizadas o canceladas.';
  };

  const getToastIcon = () => {
    if (!globalToast) return null;
    const lowerToast = globalToast.toLowerCase();
    if (lowerToast.includes('error')) return <AlertCircle size={18} />;
    if (lowerToast.includes('historial') || lowerToast.includes('gracias')) return <CheckCircle2 size={18} />;
    return <MessageCircle size={18} />;
  };

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: '16px', backgroundColor: '#0F172A', minHeight: '100vh' }}>
        <div style={{ width: 40, height: 40, border: '4px solid rgba(59, 130, 246, 0.2)', borderTopColor: '#3B82F6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <span style={{ fontSize: '15px', color: '#94A3B8', fontWeight: 600 }}>Cargando tus citas...</span>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, backgroundColor: '#0F172A', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <header style={{
        display: 'flex', alignItems: 'center', backgroundColor: '#0F172A', padding: '16px 20px',
        borderBottom: '1px solid #1E293B', zIndex: 10
      }}>
        <button onClick={() => navigate('/app/search')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '8px' }}>
          <ArrowLeft size={24} color="#FFFFFF" />
        </button>
        <h1 style={{ fontSize: '20px', fontWeight: 900, color: '#FFFFFF', margin: 0, letterSpacing: '-0.3px' }}>Mis Citas</h1>
      </header>

      {/* Selector de Pestañas (Diseño Ergonómico Grande) */}
      <div style={{ display: 'flex', padding: '8px', backgroundColor: '#0F172A', borderBottom: '1px solid #1E293B', gap: '4px' }}>
        {(['por_pagar', 'pendientes', 'proximas', 'historial'] as const).map(tab => {
          const isActive = activeTab === tab;
          let label = '';
          if (tab === 'por_pagar') {
            label = contarPorPagar > 0 ? `Pagar (${contarPorPagar})` : 'Pagar';
          } else if (tab === 'pendientes') {
            label = 'Pendiente';
          } else if (tab === 'proximas') {
            label = 'Próximas';
          } else {
            label = 'Historial';
          }

          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: '12px 6px',
                borderRadius: '12px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: isActive ? '#1E293B' : 'transparent',
                color: isActive ? '#3B82F6' : '#64748B',
                fontSize: '14px',
                fontWeight: isActive ? 800 : 600,
                transition: 'all 0.2s',
                textAlign: 'center'
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Lista de Citas */}
      <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto' }}>
        {activeTab === 'historial' && filtradas.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '-4px' }}>
            <button
              onClick={handleLimpiarHistorial}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: 'rgba(239, 68, 68, 0.1)', 
                color: '#EF4444', padding: '8px 16px', borderRadius: '100px', border: 'none', 
                cursor: 'pointer', fontSize: '13px', fontWeight: 700, transition: 'background-color 0.2s'
              }}
            >
              <Trash2 size={14} />
              Limpiar Historial
            </button>
          </div>
        )}

        {filtradas.length > 0 ? (
          <>
            {filtradas.map(cita => (
              <CitaCard 
                key={cita.cita_id} 
                cita={cita} 
                onExpire={() => cargarCitas(true)} 
                onPagar={handlePagar} 
                currentUserId={currentUserId}
                onShowToast={showToast}
              />
            ))}
            {activeTab === 'historial' && hasMore && (
              <button
                onClick={() => cargarCitas(false, true)}
                style={{
                  backgroundColor: 'rgba(59, 130, 246, 0.1)',
                  color: '#3B82F6',
                  fontWeight: 700,
                  fontSize: '14px',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  alignSelf: 'center',
                  marginTop: '8px',
                  transition: 'background-color 0.2s'
                }}
              >
                Cargar más citas
              </button>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, paddingTop: '80px', gap: '16px' }}>
            <Calendar size={64} color="#334155" />
            <div style={{ textAlign: 'center' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 800, color: '#E2E8F0', margin: '0 0 6px 0' }}>
                {getEmptyStateTitle()}
              </h2>
              <p style={{ fontSize: '14px', color: '#64748B', lineHeight: '20px', padding: '0 32px', margin: 0 }}>
                {getEmptyStateDescription()}
              </p>
            </div>
            {activeTab === 'por_pagar' && (
              <button 
                onClick={() => navigate('/app/search')}
                style={{
                  backgroundColor: 'rgba(59, 130, 246, 0.15)',
                  color: '#3B82F6',
                  fontWeight: 700,
                  fontSize: '14px',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: 'pointer',
                  marginTop: '12px'
                }}
              >
                Buscar Especialistas
              </button>
            )}
          </div>
        )}
      </div>

      {/* Modal de Confirmación Estético */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.75)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          zIndex: 1000, padding: '24px', animation: 'fadeIn 0.2s ease-out'
        }}>
          <div style={{
            backgroundColor: '#1E293B', borderRadius: '24px', padding: '32px 24px',
            width: '100%', maxWidth: '360px', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.3)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
            position: 'relative'
          }}>
            <button 
              onClick={() => setShowConfirmModal(false)}
              style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', color: '#64748B', cursor: 'pointer' }}
            >
              <X size={20} />
            </button>

            <div style={{
              width: '64px', height: '64px', borderRadius: '32px', backgroundColor: 'rgba(239, 68, 68, 0.1)',
              display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '20px'
            }}>
              <Trash2 size={32} color="#EF4444" />
            </div>

            <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#FFFFFF', margin: '0 0 12px 0' }}>¿Limpiar Historial?</h2>
            <p style={{ fontSize: '15px', color: '#94A3B8', margin: '0 0 32px 0', lineHeight: '22px' }}>
              Esto eliminará estas citas de tu vista actual.
            </p>

            <div style={{ display: 'flex', gap: '12px', width: '100%' }}>
              <button 
                onClick={() => setShowConfirmModal(false)}
                style={{
                  flex: 1, padding: '14px', borderRadius: '14px', backgroundColor: '#334155',
                  color: '#FFFFFF', border: 'none', fontWeight: 700, fontSize: '15px', cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button 
                onClick={confirmarLimpiarHistorial}
                style={{
                  flex: 1, padding: '14px', borderRadius: '14px', backgroundColor: '#EF4444',
                  color: '#FFFFFF', border: 'none', fontWeight: 700, fontSize: '15px', cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                }}
              >
                Sí, limpiar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Global (Notificación In-App) */}
      {globalToast && (
        <div style={{
          position: 'fixed', top: '24px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: globalToast.toLowerCase().includes('error') ? '#EF4444' : '#2563EB', 
          color: '#FFFFFF', padding: '12px 24px',
          borderRadius: '100px', fontSize: '14px', fontWeight: 700,
          boxShadow: globalToast.toLowerCase().includes('error') ? '0 10px 25px rgba(239, 68, 68, 0.4)' : '0 10px 25px rgba(37, 99, 235, 0.4)',
          display: 'flex', alignItems: 'center', gap: '10px',
          zIndex: 9999, animation: 'toastSlideDown 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
        }}>
          {getToastIcon()}
          {globalToast}
        </div>
      )}

      <style>{`
        @keyframes spin { 100% { transform: rotate(360deg); } }
        @keyframes toastSlideDown {
          from { transform: translate(-50%, -20px); opacity: 0; }
          to { transform: translate(-50%, 0); opacity: 1; }
        }
        .pulse-timer {
          animation: pulse 2s infinite;
        }
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.4; }
          100% { opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
